import { z } from 'zod';
/**
 * The one colour palette, shared by tags, expense categories, income categories
 * and projects.
 *
 * ── WHY A TOKEN AND NOT A HEX STRING ──
 * "Let the user pick a colour" is the obvious answer and it is wrong here for
 * three reasons that are facts rather than taste:
 *
 * 1. **Dark mode is real in this product.** `globals.css` carries a full
 *    `.dark` token block. A user-chosen `#FFE600` is invisible on the light
 *    card and a searchlight on the dark one, and there is no render-time
 *    contrast maths that rescues an arbitrary hue without computing a
 *    foreground colour per chip per theme. A slot name can re-theme; a stored
 *    hex cannot.
 * 2. **The badge idiom is a tint plus matching text.** `Badge` renders
 *    `bg-<tone>/10 text-<tone>`, which only works when a colour has a validated
 *    foreground pair. Ten tokens is twenty class pairs defined once per theme.
 * 3. **Thirty user-chosen colours are not thirty distinguishable colours.**
 *    Under deuteranopia - roughly one man in twelve - red, green, orange and
 *    lime collapse toward the same yellow-brown. A picker sells a precision
 *    that does not exist.
 *
 * Cyan is deliberately absent: it sits too close to both blue and teal once the
 * blue-green range flattens, and nine well-separated hues plus a neutral beats
 * ten crowded ones.
 *
 * Stored as `varchar(20)` rather than a `mysqlEnum` so adding a colour is not a
 * migration.
 */
export const CATEGORY_COLOURS = [
    'slate',
    'blue',
    'teal',
    'green',
    'lime',
    'amber',
    'orange',
    'red',
    'pink',
    'violet',
];
/** The fallback, and the colour a row gets when nobody chooses one. */
export const DEFAULT_COLOUR = 'slate';
export const colourSchema = z.enum(CATEGORY_COLOURS);
/**
 * Read a stored colour, never throwing.
 *
 * A row whose `colour` is hand-set to something outside the palette - by a data
 * repair, by a restored backup, by a column added before this list settled -
 * resolves to `slate`. The same fail-soft posture `resolvePermissions` takes,
 * and for the same reason: a bad colour must never be able to 500 a report.
 * Writes are still validated against the enum, so a client cannot introduce
 * one.
 */
export function resolveColour(raw) {
    return typeof raw === 'string' && CATEGORY_COLOURS.includes(raw)
        ? raw
        : DEFAULT_COLOUR;
}
/**
 * The colour a new row gets, by position in the list it is joining.
 *
 * Round-robin over an order that keeps confusable hues apart, so a user who
 * never opens the colour picker still ends up with a distinguishable set. It
 * skips `slate`, which is reserved for the "Uncategorised" row every breakdown
 * renders - a real category sharing that colour would read as the leftovers.
 */
const ROTATION = [
    'blue',
    'amber',
    'teal',
    'pink',
    'green',
    'orange',
    'violet',
    'lime',
    'red',
];
export function colourForIndex(index) {
    const slot = ROTATION[((index % ROTATION.length) + ROTATION.length) % ROTATION.length];
    return slot ?? DEFAULT_COLOUR;
}
/**
 * The surfaces a chip is drawn on. `--card` in both themes.
 *
 * Here rather than in the CSS because the contrast test needs them, and a
 * contrast test whose background is a guess proves nothing.
 */
export const CARD_SURFACE = {
    light: [0, 0, 100],
    dark: [222.2, 40, 11],
};
/**
 * The full-strength value of every palette slot, per theme.
 *
 * ── WHY THIS IS THE SOURCE OF TRUTH AND THE CSS IS GENERATED FROM IT ──
 * These lived only in `globals.css`, where nothing could test them - and the
 * comment two lines above them asserted every slot cleared 4.5:1 while amber
 * measured 4.15:1 against the card and 3.60:1 against the tint its own text
 * sits on. An assertion in a comment is a hope; this table plus
 * `paletteContrastFailures` is the same claim as an assertion a build can fail.
 *
 * ── WHAT THE NUMBERS HAVE TO CLEAR ──
 * A chip renders the slot's colour as TEXT on a 12% wash of the same slot over
 * the card. Two hues that both pass against the card can still fail against
 * their own tint, because the wash pulls the background toward the text - which
 * is exactly how the first set passed review and failed measurement. So the
 * binding requirement is text-on-tint, and text-on-card follows from it.
 */
export const PALETTE_TOKENS = {
    slate: { light: [215, 20, 34], dark: [215, 18, 72] },
    blue: { light: [221, 75, 42], dark: [217, 85, 74] },
    teal: { light: [173, 70, 24], dark: [172, 65, 62] },
    green: { light: [142, 65, 25], dark: [142, 58, 64] },
    lime: { light: [88, 60, 24], dark: [88, 55, 64] },
    amber: { light: [38, 85, 28], dark: [40, 88, 68] },
    orange: { light: [22, 85, 34], dark: [24, 90, 70] },
    red: { light: [0, 72, 41], dark: [0, 80, 74] },
    pink: { light: [330, 70, 38], dark: [332, 78, 74] },
    violet: { light: [265, 65, 46], dark: [265, 80, 78] },
};
/** The alpha a chip's background is washed at. Matches `ColourChip`. */
export const CHIP_TINT_ALPHA = 0.12;
/** HSL to sRGB, each channel 0-1. The standard conversion, no rounding. */
export function hslToRgb([h, s, l]) {
    const sat = s / 100;
    const light = l / 100;
    const c = (1 - Math.abs(2 * light - 1)) * sat;
    const hp = (((h % 360) + 360) % 360) / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    const [r, g, b] = hp < 1 ? [c, x, 0]
        : hp < 2 ? [x, c, 0]
            : hp < 3 ? [0, c, x]
                : hp < 4 ? [0, x, c]
                    : hp < 5 ? [x, 0, c]
                        : [c, 0, x];
    const m = light - c / 2;
    return [r + m, g + m, b + m];
}
/** WCAG relative luminance. */
export function relativeLuminance(rgb) {
    const [r, g, b] = rgb.map((channel) => channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG contrast ratio between two sRGB colours. */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/** `fg` at `alpha` composited over `bg` - what a tinted chip background is. */
export function composite(fg, bg, alpha) {
    return [
        fg[0] * alpha + bg[0] * (1 - alpha),
        fg[1] * alpha + bg[1] * (1 - alpha),
        fg[2] * alpha + bg[2] * (1 - alpha),
    ];
}
/**
 * Every slot that does not clear the ratio its text needs, in either theme.
 *
 * Returns the failures rather than throwing, so a test can name all of them at
 * once - fixing a palette one error message at a time is how the fourth one
 * gets left behind.
 */
export function paletteContrastFailures(required = 4.5) {
    const failures = [];
    for (const theme of ['light', 'dark']) {
        const card = hslToRgb(CARD_SURFACE[theme]);
        for (const colour of CATEGORY_COLOURS) {
            const text = hslToRgb(PALETTE_TOKENS[colour][theme]);
            const tint = composite(text, card, CHIP_TINT_ALPHA);
            for (const [against, background] of [
                ['card', card],
                ['tint', tint],
            ]) {
                const ratio = contrastRatio(text, background);
                if (ratio < required)
                    failures.push({ colour, theme, against, ratio, required });
            }
        }
    }
    return failures;
}
/**
 * The palette as CSS custom-property declarations, for one theme.
 *
 * The app injects these at mount rather than hard-coding them in `globals.css`,
 * so there is ONE table and it is the tested one. The tokens only ever style
 * elements React renders, so there is no unstyled-flash to trade against.
 */
export function paletteCssVariables(theme) {
    return CATEGORY_COLOURS.map((colour) => {
        const [h, s, l] = PALETTE_TOKENS[colour][theme];
        return `--cat-${colour}: ${h} ${s}% ${l}%;`;
    }).join(' ');
}
//# sourceMappingURL=classification.js.map